import { Receipt, CreditCard, Calculator } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels } from "@/i18n";
import { useState } from "react";
import TermsDialog from "@/components/footer/terms_dialog";

interface CostCalculatorProps {
  price: number;
}

const CostCalculator = ({ price }: CostCalculatorProps) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const { language } = useLanguage();
  const labels = carDetailsLabels(language).costCalculator;

  return (
    <div className="space-y-4">
      {price && (
      <div
        className={`${isDark ? "bg-black/20" : "bg-gray-50"} p-4 rounded-lg`}
      >
        <div className="flex items-center gap-3 mb-3">
          <Receipt className="w-5 h-5 text-accent" />
          <h3
            className={`font-medium ${isDark ? "text-white" : "text-gray-800"}`}
          >
            {labels.salesPrice}
          </h3>
        </div>
        <p className={isDark ? "text-gray-300" : "text-gray-700"}>
          {price.toLocaleString()} {labels.currency}
        </p>
      </div>
      )}

      <div
        className={`${
          isDark ? "bg-black/20" : "bg-gray-50"
        } p-4 w-full rounded-lg`}
      >
        <div className="flex items-center gap-3 mb-3">
          <CreditCard className="w-5 h-5 text-accent" />
          <h3
            className={`font-medium ${isDark ? "text-white" : "text-gray-800"}`}
          >
            {labels.fees}
          </h3>
        </div>
        <ul className="space-y-2 w-full">
          <li className="flex items-start justify-between text-primary/60">
            <span>{labels.deliveryFee}</span>
            <span className="font-medium text-right ml-2">
              {labels.deliveryFeeDepends}
            </span>
          </li>
          <li className="flex items-start justify-between text-primary/60">
            <span>{labels.buyerFee}</span>
            <button
              onClick={() => setShowTermsDialog(true)}
              className="font-medium text-right ml-4 text-accent hover:underline cursor-pointer"
            >
              {labels.seePriceList}
            </button>
          </li>
        </ul>
      </div>

      {price && (
      <div className={`bg-primary/10 p-4 rounded-lg`}>
        <div className="flex items-center gap-3 mb-3">
          <Calculator className="w-5 h-5 text-accent" />
          <h3
            className={`font-medium ${isDark ? "text-white" : "text-gray-800"}`}
          >
            {labels.totalExcl}
          </h3>
        </div>
        <p
          className={`${
            isDark ? "text-white" : "text-gray-900"
          } font-medium text-accent`}
        >
          {price.toLocaleString()} kr
        </p>
      </div>
      )}

      <TermsDialog
        open={showTermsDialog}
        onOpenChange={setShowTermsDialog}
        initialTab="pricing"
      />
    </div>
  );
};

export default CostCalculator;
